// What lies over the whole window (a dialog's dimmed ground, an image
// viewer's), told to the host that draws around the screens (the outside's
// `cover`): in a browser the ground covers the whole window, but a host's own
// parts (the desktop app's rail and sidebar beside the cloud's page) lie
// outside the page, so the host lays the same shade over them.
//
// Promise:
// - The host hears the one on top (the last opened still open), and null once
//   none is; the same cover is not sent twice in a row.
// - Told before the page paints it (a layout effect, sent at the end of that
//   task), so the two halves come up together; one closing as another opens
//   sends only the new one.
// - A press on the host's part (`cover-pressed`) does what a press beside the
//   one on top does (closes it), or nothing when that one does not close so.
// - Without a host (a plain browser) nothing is sent.
import { useContext, useLayoutEffect, useRef } from "react";
import { MeAppContext } from "../../app/context";
import { SHELL_CONTRACT_VERSION, type ShellCover } from "./contract";
import type { ShellHost } from "./outside";

interface Layer {
  shade: number;
  press: { current: (() => void) | null };
}

interface Stack {
  layers: Layer[];
  /** Listening for `cover-pressed` (while any layer is open). */
  off: (() => void) | null;
  /** The cover last sent, as JSON. */
  sent: string;
  /** A tell is due at the end of this task. */
  due: boolean;
}

// frontend-development: one stack per host (each app has its own host), not one for the page: which cover is on top across the dialogs and viewers open at once
const stacks = new WeakMap<ShellHost, Stack>();

/**
 * At the end of the task (after the commit, before paint): one closing and
 * another opening in the same commit send only the one now on top.
 */
function tellSoon(host: ShellHost, stack: Stack) {
  if (stack.due) return;
  stack.due = true;
  queueMicrotask(() => {
    stack.due = false;
    tell(host, stack);
  });
}

function tell(host: ShellHost, stack: Stack) {
  const top = stack.layers.at(-1);
  const cover: ShellCover | null = top
    ? { shade: top.shade, dismiss: top.press.current !== null }
    : null;
  const key = JSON.stringify(cover);
  if (key === stack.sent) return;
  stack.sent = key;
  host.post({ version: SHELL_CONTRACT_VERSION, type: "cover", cover });
}

/**
 * While `open`, the window is covered with black at `shade`; `onPress` is what
 * a press beside it does (null: nothing).
 */
export function useOutsideCover(
  open: boolean,
  shade: number,
  onPress: (() => void) | null,
): void {
  const host = useContext(MeAppContext)?.extensions.shellHost ?? null;
  const press = useRef(onPress);
  press.current = onPress;
  const dismiss = onPress !== null;
  // frontend-development: not a fetch; tells the host what lies over the window.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `dismiss` is read through `press` in tell()
  useLayoutEffect(() => {
    if (!open || !host) return;
    let stack = stacks.get(host);
    if (!stack) {
      stack = { layers: [], off: null, sent: "null", due: false };
      stacks.set(host, stack);
    }
    const s = stack;
    const layer: Layer = { shade, press };
    s.layers.push(layer);
    s.off ??= host.subscribe((message) => {
      if (message.type === "cover-pressed") s.layers.at(-1)?.press.current?.();
    });
    tellSoon(host, s);
    return () => {
      s.layers.splice(s.layers.indexOf(layer), 1);
      if (s.layers.length === 0) {
        s.off?.();
        s.off = null;
      }
      tellSoon(host, s);
    };
  }, [open, host, shade, dismiss]);
}
